import React from "react";
import { formatSecondsBrief, pct } from "../../../../utils/oversightFormat";
import { chatEngagement } from "./coachOverviewDerive";

const VALUE_TONE = {
  neutral: "text-gray-800",
  success: "text-success",
  danger: "text-danger-dark",
};

/**
 * Render a metric's value according to its `kind`. Null always reads "—":
 * "we don't know" and "zero" are different answers and must look different.
 */
function formatValue(metric) {
  const { value, kind, denominator } = metric;
  if (value == null) return "—";
  switch (kind) {
    case "seconds":
      return formatSecondsBrief(value);
    case "percent":
      return pct(value);
    case "decimal":
      return value;
    case "fraction":
      return (
        <>
          {value}
          {denominator != null && (
            <span className="text-sm font-normal text-gray-400"> / {denominator}</span>
          )}
        </>
      );
    default:
      return value.toLocaleString();
  }
}

/**
 * "Chat engagement" — the primary-signal panel on the Overview tab.
 *
 * Six metrics, each with the sentence that says what it means, because a bare
 * number does not tell a manager what to do about it.
 *
 * Two metrics from the design are deliberately absent rather than
 * approximated: "clients who write back" needs a count of distinct rooms a
 * client replied in, and "conversations they opened" needs initiation
 * attribution. Both need a backend change; the footnote says so.
 *
 * @param {Object} props
 * @param {Object|null|undefined} props.payload - The coach analytics response.
 * @param {number|null} [props.assignedClients] - Roster length; the only
 *   authoritative client count (see `coachOverviewDerive`).
 * @param {React.ReactNode} [props.chart] - The volume chart rendered under the grid.
 */
export function ChatEngagementCard({ payload, assignedClients, chart }) {
  const metrics = chatEngagement(payload, { assignedClients });
  const p90 = payload?.totals?.p90_first_response_seconds;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex flex-wrap items-baseline gap-2.5 mb-4">
        <h2 className="text-base font-bold text-gray-900">Chat engagement</h2>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-light-orange text-orange-primary">
          Primary signal
        </span>
        <span className="sm:ml-auto text-[11px] text-gray-400">
          Coaching chats only. Support tickets are counted separately.
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 mb-5">
        {metrics.map((m) => (
          <div key={m.id}>
            <p className={`text-[26px] leading-8 font-bold ${VALUE_TONE[m.tone] || VALUE_TONE.neutral}`}>
              {formatValue(m)}
            </p>
            <p className="mt-0.5 text-[11px] uppercase tracking-wide text-gray-500">{m.label}</p>
            {m.sentence && <p className="mt-1 text-[11px] text-gray-400">{m.sentence}</p>}
            {m.id === "replySpeed" && p90 != null && (
              <p className="mt-0.5 text-[11px] text-gray-400">
                p90 {formatSecondsBrief(p90)}
              </p>
            )}
          </div>
        ))}
      </div>

      {chart}

      <p className="mt-3 text-[11px] text-gray-400">
        Room counts cover every room type, including support. "Clients who write back" and
        "conversations they opened" need distinct-client and initiation counts the analytics
        rollup does not compute yet, so they are not shown.
      </p>
    </div>
  );
}

export default ChatEngagementCard;
